/**
 * The organization's rules on targets in its settings (`policy.targets`,
 * `org/targets-policy.ts` in contracts; 2026-10-26, Claude Design « Proposition
 * 26 octobre - Profils d'environnement »), under Network: what a member's
 * machine may not do with a target — a preprod, a production — whatever its
 * workspaces say. Without them, each target has the mode its person chose.
 *
 * Policy: nothing on a member's machine inherits or overrides it. Published
 * with the rest of the settings, as the « Targets » line of the diff. The
 * targets the organization shares follow it (`shared-targets-settings.tsx`).
 */
import type React from 'react';
import { EMPTY_ORG_POLICY, policyOf, type OrgPolicy } from '@airlock/contracts/org/policy';
import type { OrgTargets } from '@airlock/contracts/org/targets-policy';
import { SettingToggle } from '../../components/settings-row';
import { color } from '../../theme/tokens';
import type { OrgSettings } from '../../lib/types';
import { ListBlock } from './list-settings';
import { SharedTargets } from './shared-targets-settings';

const ROWS: { key: keyof OrgTargets; label: string; hint: string }[] = [
  {
    key: 'noProduction',
    label: 'No production target',
    hint: 'No session of a member starts on a target marked Production, on any machine.'
  },
  {
    key: 'forceHost',
    label: 'Every target through the host',
    hint: 'Direct access is off: the agent never reaches a target, and no target’s secret is ever put in a machine. It asks for each read, which a person runs.'
  },
  {
    key: 'noAlwaysRun',
    label: 'Ask for every read',
    hint: 'Always run is off: a person runs each read through the host from its card.'
  },
  {
    key: 'forceReview',
    label: 'Review every result',
    hint: 'Review results before sending is on for every target: a person reads each result, already anonymized, before the agent gets it — whatever a member ticked.'
  }
];

export function TargetsPolicy({
  settings,
  editable,
  onChange
}: {
  settings: OrgSettings;
  editable: boolean;
  onChange: (next: OrgPolicy) => void;
}): React.JSX.Element {
  const policy = policyOf(settings);
  const targets: OrgTargets = policy.targets;
  const put = (patch: Partial<OrgTargets>): void =>
    onChange({ ...EMPTY_ORG_POLICY, ...policy, targets: { ...targets, ...patch } });
  return (
    <>
      {ROWS.map((row) => (
        <ListBlock key={row.key} label={row.label} hint={row.hint}>
          {editable ? (
            <SettingToggle on={targets[row.key]} onToggle={() => put({ [row.key]: !targets[row.key] })} />
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: color.inkMuted }}>{targets[row.key] ? 'Yes' : 'No'}</p>
          )}
        </ListBlock>
      ))}
      <SharedTargets settings={settings} editable={editable} onChange={onChange} />
    </>
  );
}
